"use client";

/**
 * Broad-search "ask for the exact product" screen (Step 8, 2026-08-01).
 *
 * Shown when the backend couldn't confidently name a best 5-6 for a broad query
 * (scattered results, too few good products, or a timeout). Honest fallback:
 * ask for the exact product rather than guess. Uses the real SearchAgainBar and
 * the .cmp-surface design system so it reads as one with the app.
 *
 * The direction chips append a budget to the current query and re-search, which
 * gives the ranker a tighter set to work with (the backend applies the budget
 * as a hard filter). The primary path stays the search bar - type the exact
 * product and it goes straight to the comparison.
 */

import type { Route } from "next";
import { useRouter } from "next/navigation";
import { useTransition } from "react";

import { SearchAgainBar } from "./SearchAgainBar";

interface Props {
  query: string;
}

const BUDGETS = ["Under $100", "Under $250", "Under $500"] as const;

export function AskExact({ query }: Props) {
  const router = useRouter();
  const [, startTransition] = useTransition();

  // "Under $250" -> re-search "<query> under $250" (the ranker applies the cap).
  // 2026-09-22 (owner-caught): a budget already on the query is REPLACED, never
  // stacked. The production log carried "... under $100 under $100 under $100"
  // after three clicks on the same chip.
  const refine = (label: string) => {
    const base = query.trim().replace(/\s+under\s+\$\s?[\d,]+(?:\.\d+)?\s*$/i, "").trim();
    const refined = `${base} ${label.toLowerCase()}`.trim();
    startTransition(() =>
      router.push(`/compare?q=${encodeURIComponent(refined)}` as Route),
    );
  };

  return (
    <main className="cmp-surface mx-auto min-h-screen max-w-3xl bg-background px-6 pb-16">
      <header className="mt-8 max-w-2xl">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          Tell me the exact product and I&rsquo;ll find the best price.
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          &ldquo;{query}&rdquo; covers a lot of ground. Give me the exact model,
          or the style and budget, and I&rsquo;ll compare the True Final Price
          across every Australian shop.
        </p>
      </header>

      <div className="mt-6">
        <SearchAgainBar
          initialQuery=""
          placeholder="Exact product name&hellip;"
        />
      </div>

      <div className="mt-8">
        <div className="text-[12px] uppercase tracking-wider text-muted-foreground">
          Or narrow it down by budget
        </div>
        <div className="mt-3 flex flex-wrap gap-2.5">
          {BUDGETS.map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => refine(b)}
              className="cursor-pointer rounded-full border border-border bg-secondary px-4 py-2 text-[13px] text-muted-foreground transition hover:border-amber-300/40 hover:text-amber-200"
            >
              {b}
            </button>
          ))}
        </div>
      </div>
    </main>
  );
}
